import {
  eachDayOfInterval,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
} from 'date-fns';

export const DISPLAY_MONTH_YEARS_BACK = 10;


export type DateRangeDraft = { from?: Date; to?: Date } | undefined;
export type LooseDateRange = { from?: Date | null; to?: Date | null } | null | undefined;

export function sanitizeDay(value: Date | null | undefined, max: Date): Date | undefined {
  if (!value) return undefined;
  const time = value instanceof Date ? value.getTime() : NaN;
  if (Number.isNaN(time)) return undefined;
  const day = startOfDay(value);
  return isAfter(day, max) ? max : day;
}

export function seedDraftFromValue(value: LooseDateRange, max: Date): DateRangeDraft {
  let from = sanitizeDay(value?.from, max);
  let to = sanitizeDay(value?.to, max);
  if (from && to && isAfter(from, to)) [from, to] = [to, from];
  if (!from && !to) return undefined;
  return { from: from ?? to, to: from && to ? to : undefined };
}

export function maxDisplayMonth(today: Date): Date {
  return startOfMonth(today);
}

export function minDisplayMonth(
  today: Date,
  yearsBack = DISPLAY_MONTH_YEARS_BACK,
): Date {
  return new Date(today.getFullYear() - yearsBack, 0, 1);
}

export function clampDisplayMonth(
  candidate: Date,
  bounds: { min: Date; max: Date },
): Date {
  const month = startOfMonth(candidate);
  if (isAfter(month, bounds.max)) return bounds.max;
  if (isBefore(month, bounds.min)) return bounds.min;
  return month;
}

export function seedVisibleMonths(
  value: LooseDateRange,
  today: Date,
): { left: Date; right: Date } {
  const bounds = { min: minDisplayMonth(today), max: maxDisplayMonth(today) };
  const seeded = seedDraftFromValue(value, today);

  if (!seeded?.from) {
    return {
      left: clampDisplayMonth(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        bounds,
      ),
      right: bounds.max,
    };
  }

  let left = clampDisplayMonth(seeded.from, bounds);
  let right = clampDisplayMonth(seeded.to ?? seeded.from, bounds);

  if (left.getTime() === right.getTime()) {
    const prev = new Date(left.getFullYear(), left.getMonth() - 1, 1);
    if (!isBefore(prev, bounds.min)) left = prev;
  }

  return { left, right };
}

export function nextDraftAfterPick(prev: DateRangeDraft, clicked: Date): DateRangeDraft {
  const day = startOfDay(clicked);
  if (!prev?.from || (prev.from && prev.to)) return { from: day, to: undefined };
  let from = startOfDay(prev.from);
  let to = day;
  if (isAfter(from, to)) [from, to] = [to, from];
  return { from, to };
}

export function buildPreviewRange(from: Date, hover: Date): { start: Date; end: Date } | null {
  let start = startOfDay(from);
  let end = startOfDay(hover);
  if (isSameDay(start, end)) return null;
  if (isAfter(start, end)) [start, end] = [end, start];
  return { start, end };
}

export function shouldPreviewOnHover(draft: DateRangeDraft): boolean {
  return Boolean(draft?.from && !draft?.to);
}

export function buildRangeModifiers(
  draft: DateRangeDraft,
  hoverDate: Date | undefined,
): Record<string, Date[]> {
  const mods: Record<string, Date[]> = {};
  if (!draft?.from) return mods;

  const from = startOfDay(draft.from);
  const preview =
    hoverDate && shouldPreviewOnHover(draft) ? buildPreviewRange(from, hoverDate) : null;

  if (preview) {
    mods.preview_start = [preview.start];
    mods.preview_end = [preview.end];
    try {
      mods.preview_middle = eachDayOfInterval({ start: preview.start, end: preview.end }).filter(
        (d) => !isSameDay(d, preview.start) && !isSameDay(d, preview.end),
      );
    } catch {
    }
    return mods;
  }

  if (draft.to) {
    const to = startOfDay(draft.to);
    mods.range_start = [from];
    mods.range_end = [to];
    try {
      mods.range_middle = eachDayOfInterval({ start: from, end: to }).filter(
        (d) => !isSameDay(d, from) && !isSameDay(d, to),
      );
    } catch {
    }
  } else {
    mods.range_start = [from];
  }
  return mods;
}

export function normalizeAppliedRange(draft: DateRangeDraft): { from: Date; to: Date } | null {
  if (!draft?.from) return null;
  let from = startOfDay(draft.from);
  let to = endOfDay(draft.to ?? draft.from);
  if (isAfter(from, to)) {
    const swappedFrom = startOfDay(to);
    const swappedTo = endOfDay(from);
    from = swappedFrom;
    to = swappedTo;
  }
  return { from, to };
}

export function canApplyDraft(draft: DateRangeDraft): boolean {
  return Boolean(draft?.from);
}

export function canClearDraft(draft: DateRangeDraft, appliedExists: boolean): boolean {
  return Boolean(draft?.from || draft?.to || appliedExists);
}

export function stepDisplayMonths(
  left: Date,
  right: Date,
  delta: number,
  bounds: { min: Date; max: Date },
): { left: Date; right: Date } | null {
  const shift = (m: Date) => new Date(m.getFullYear(), m.getMonth() + delta, 1);
  const nextLeft = shift(left);
  const nextRight = shift(right);
  const outOfBounds = (m: Date) => isAfter(m, bounds.max) || isBefore(m, bounds.min);
  if (outOfBounds(nextLeft) || outOfBounds(nextRight)) return null;
  return { left: nextLeft, right: nextRight };
}

export function canStepDisplayMonths(
  left: Date,
  right: Date,
  delta: number,
  bounds: { min: Date; max: Date },
): boolean {
  return stepDisplayMonths(left, right, delta, bounds) !== null;
}
